import { z } from 'zod';
import { CONNECTORS } from '../catalog/connectors';
import { isIsoTimestamp } from '../time';
import { timestampSchema } from './common';
import {
  callEndReason,
  callParticipantType,
  callReadyBody,
  callRecordingNoteMetadata,
  callStatus,
  createCallBody,
  createCallResponse,
} from './calls';

// ---------------------------------------------------------------------------
// Eval calls — `/api/evals/calls` (realtime voice calls, gh #4418; plan §12.3)
//
// An EVAL CALL is a real voice call placed by a machine caller (the CLI's call-eval runner)
// for one eval journey whose responder is a `pilot_voice` agent. The runner holds the WebRTC
// media itself — its synthesized caller audio goes to the provider and the voice's audio comes
// back to it — so the backend is never on the call's audio path: these routes create the
// call, mark it ready, hang it up, read its record and close it.
//
// The call runs on the end user's call-creation core (the rollout gate, the §1 predicate, the
// free-plan quota, the provider session, call control), with three differences:
// - the grant: `loadHub` on the hub, as every eval route takes, never end-user access; and the
//   `/api/evals/*` rate budget, so a suite's repeated runs never draw on the end user's
//   `/api/calls` create budget;
// - the conversation: a fresh `is_eval` conversation per call, whose caller is a synthetic,
//   state-only user derived from the call id (never the operator), so an eval call cannot
//   touch the operator's own chat or user-scope state;
// - the voice agent: the journey's responder, which must be an enabled `pilot_voice` agent.
//
// Eval calls bill as any `is_eval` run does: each call turn's initiation and the call meter's
// connected minutes, on the org; the provider's minutes on the org's own key. `max_call_seconds`
// is the server-side ceiling a runner's per-run cost cap puts on the call's length.
// ---------------------------------------------------------------------------

/** An instant in the canonical form only (AGENTS.md › Timestamps). */
const evalCallInstant = timestampSchema.refine(isIsoTimestamp, 'Expected a canonical ISO timestamp');

/** Shortest ceiling a runner may set on an eval call: the greeting and one question. */
export const EVAL_CALL_MIN_SECONDS = 30;

/**
 * Longest ceiling: the realtime connectors' own maximum call length (`max_call_minutes`), read
 * off the catalog so the two cannot drift. The call runs for the smaller of a runner's ceiling
 * and its voice agent's own maximum either way.
 */
export const EVAL_CALL_MAX_SECONDS = 60 * Math.max(
  0,
  ...CONNECTORS
    .filter((connector) => connector.connector_type === 'Realtime')
    .map((connector) => Number((connector.agent_settings_schema?.max_call_minutes as { max?: unknown } | undefined)?.max) || 0),
);

/**
 * The most characters one of the voice's fixed lines can hold as its builder writes it (the
 * realtime connectors' `text` settings' `maxLength`: the greeting, the cues, the apologies), read
 * off the catalog like the ceiling above: what a runner sizes its waits for the voice's speech by.
 */
export const EVAL_CALL_SPOKEN_LINE_MAX_CHARS = Math.max(
  0,
  ...CONNECTORS
    .filter((connector) => connector.connector_type === 'Realtime')
    .flatMap((connector) => Object.values((connector.agent_settings_schema ?? {}) as Record<string, { type?: unknown; maxLength?: unknown }>))
    .filter((field) => field.type === 'text')
    .map((field) => Number(field.maxLength) || 0),
);

const hubScoped = { hub_id: z.string().uuid() };

// ---------------------------------------------------------------------------
// POST /api/evals/calls — create an eval call
// ---------------------------------------------------------------------------

export const createEvalCallBody = z.object({
  ...hubScoped,
  /** The journey the call plays. Its responder must be an enabled `pilot_voice` agent. */
  journey_id: z.string().uuid(),
  /** The runner's SDP offer, sent to the provider unchanged (bounded as the end user's is). */
  sdp_offer: createCallBody.shape.sdp_offer,
  /**
   * Ceiling on the call's length, in seconds: the provider minutes this run may spend. The
   * call runs for the smaller of this and the voice agent's own maximum. Absent: the agent's.
   */
  max_call_seconds: z.number().int().min(EVAL_CALL_MIN_SECONDS).max(EVAL_CALL_MAX_SECONDS).optional(),
});
export type CreateEvalCallBody = z.infer<typeof createEvalCallBody>;

/** Parse the route's answer through this: it is an allowlist of these fields. */
export const createEvalCallResponse = z.object({
  call_id: z.string().uuid(),
  /** The fresh `is_eval` conversation the call runs in. */
  conversation_id: z.string().uuid(),
  /** The provider's SDP answer, for the runner's `setRemoteDescription`. */
  sdp_answer: createCallResponse.shape.sdp_answer,
  /** The `pilot_voice` agent the call runs on: the journey's responder. */
  voice_agent_id: z.string().uuid(),
  /** The call's maximum length as started: the ceiling the server enforces. */
  max_call_seconds: z.number().int().positive(),
  /**
   * The call meter's price when the call started, in operations per connected minute begun
   * (`platform_config` `voice_call_minute_ops`); null when it could not be read. With one
   * operation per call turn, it is what a runner counts a live call's WayAI operations by.
   */
  minute_price_ops: z.number().int().nonnegative().nullable(),
  /**
   * What the runner plans its waits by, as the call started with them: whether the voice fills
   * the wait for an answer with progress cues (its agent's `progress_cues`), and how long it
   * waits for one before its timeout line (`delegation_timeout_seconds`). With the cues off
   * the wait is silent, so a pause is no sign that the answer has been said.
   */
  progress_cues: z.boolean(),
  delegation_timeout_seconds: z.number().int().positive(),
});
export type CreateEvalCallResponse = z.infer<typeof createEvalCallResponse>;

// ---------------------------------------------------------------------------
// POST /api/evals/calls/:call_id/ready  — the runner's media is connected: the voice may speak
// POST /api/evals/calls/:call_id/hangup — the runner hangs up (idempotent)
// Both answer the end user's `callResponse` (the call's summary).
// ---------------------------------------------------------------------------

export const evalCallBody = callReadyBody;
export type EvalCallBody = z.infer<typeof evalCallBody>;

// ---------------------------------------------------------------------------
// GET  /api/evals/calls/:call_id?hub_id&conversation_id — the call's record, for scoring
//      [&include_recording=true]                         — and its recording, for the web page
// POST /api/evals/calls/:call_id/finish                 — end the call, close its conversation
// ---------------------------------------------------------------------------

export const evalCallConversationQuery = z.object({
  ...hubScoped,
  conversation_id: z.string().uuid(),
});
export type EvalCallConversationQuery = z.infer<typeof evalCallConversationQuery>;

/**
 * The record's query. `include_recording=true` asks for the recording too (`recording`); only
 * the web app's eval call page asks. Without it the record carries none, so a client that keeps
 * or writes out what it reads — the CLI's runner and its `--json` report, a published CLI from
 * before the field included — never holds the recording's file key.
 */
export const evalCallRecordQuery = evalCallConversationQuery.extend({
  include_recording: z.enum(['true', 'false']).optional(),
});
export type EvalCallRecordQuery = z.infer<typeof evalCallRecordQuery>;

export const evalCallFinishBody = evalCallConversationQuery;
export type EvalCallFinishBody = z.infer<typeof evalCallFinishBody>;

/** Most utterances one record returns: a 119-minute call at pt-BR speaking rates stays under it. */
export const EVAL_CALL_RECORD_MAX_UTTERANCES = 2_000;

/** Most call turns one record returns. */
export const EVAL_CALL_RECORD_MAX_TURNS = 500;

/** Longest reply text one call turn carries in the record; a longer reply is cut. */
export const EVAL_CALL_RECORD_MAX_REPLY_CHARS = 8_000;

/** Who spoke an utterance: the caller, or the voice layer (`pilot_voice`). */
export const evalCallSpeaker = callParticipantType.extract(['user', 'voice_agent']);
export type EvalCallSpeaker = z.infer<typeof evalCallSpeaker>;

/** One finalized utterance: its row, its speaker, its text and its span on the provider session's timeline. */
export const evalCallUtterance = z.object({
  message_id: z.string(),
  speaker: evalCallSpeaker,
  text: z.string(),
  /** Milliseconds from the provider session's start. */
  start_ms: z.number().int().nonnegative(),
  end_ms: z.number().int().nonnegative(),
});
export type EvalCallUtterance = z.infer<typeof evalCallUtterance>;

/**
 * One delegation the voice raised and the call turn that answered it: the caller utterance it
 * answered (`anchor_message_id`), how it ended, and the pilot's reply — the only source the
 * voice may state a figure from.
 */
export const evalCallTurn = z.object({
  delegation_id: z.string(),
  status: z.enum(['running', 'done']),
  /** The call turn's outcome (`replied`, `held`, `no_input`, …) once it is `done`; null while it runs. */
  outcome: z.string().nullable(),
  anchor_message_id: z.string().nullable(),
  anchor_start_ms: z.number().int().nullable(),
  started_at: evalCallInstant,
  ended_at: evalCallInstant.nullable(),
  /** The pilot's reply rows as stored, its continuations' included, oldest first; cut at `EVAL_CALL_RECORD_MAX_REPLY_CHARS`. */
  reply_text: z.string().nullable(),
});
export type EvalCallTurn = z.infer<typeof evalCallTurn>;

/**
 * The eval call's recording, when its voice agent records calls (`record_calls`): the file and
 * what its note says of it (`CallRecordingNoteMetadata`), as the support inbox plays a live
 * call's. The team plays it from the eval call's page in the web app, through the conversation
 * bucket's file route. That route serves a key to any signed-in holder, so the record hands the
 * key out only when asked for it (`evalCallRecordQuery.include_recording`), under the eval grant.
 */
export const evalCallRecording = callRecordingNoteMetadata.shape.recording.extend({
  bucket: z.literal('conversation'),
  file_path: z.string().min(1),
  /** `audio/webm` (WebM Opus), or `audio/wav` for a recording finished before WebM Opus. */
  mime_type: z.string().min(1),
  file_size: z.number().int().positive(),
});
export type EvalCallRecording = z.infer<typeof evalCallRecording>;

/**
 * The web app's page for one eval call: its transcript and its recording. Both ids are route
 * segments, as the record's route needs both (`evalCallConversationQuery`). The backend builds
 * the record's `web_url` from it; the web parses the same path (`useSettingsPath`).
 */
export function evalCallWebPath(ids: { org_id: string; hub_id: string; conversation_id: string; call_id: string }): string {
  const [org, hub, conversation, call] = [ids.org_id, ids.hub_id, ids.conversation_id, ids.call_id].map(encodeURIComponent);
  return `/settings/organizations/${org}/hubs/${hub}/evals/calls/${conversation}/${call}`;
}

export const evalCallRecordResponse = z.object({
  call: z.object({
    call_id: z.string().uuid(),
    conversation_id: z.string().uuid(),
    voice_agent_id: z.string(),
    status: callStatus,
    started_at: evalCallInstant,
    active_at: evalCallInstant.nullable(),
    ended_at: evalCallInstant.nullable(),
    /** Why the call ended; null while it is live. */
    end_reason: callEndReason.nullable(),
    max_call_seconds: z.number().int(),
  }),
  /** Oldest first, at most `EVAL_CALL_RECORD_MAX_UTTERANCES`. */
  utterances: z.array(evalCallUtterance),
  /** In the order the delegations arrived, at most `EVAL_CALL_RECORD_MAX_TURNS`. */
  turns: z.array(evalCallTurn),
  /**
   * What the run charged on WayAI's meter. The conversation holds this one call only, so its
   * rows' `billable_operations` are the run's operations: each call turn's initiation, and the
   * call meter's minutes once the meter has billed them (`meter` = `billed`). While it reads
   * `pending` or `unmetered`, the minutes are not in `charged` yet.
   */
  operations: z.object({
    charged: z.number().int().nonnegative(),
    meter: z.enum(['billed', 'pending', 'unmetered']),
    /** The connected minutes the meter billed; null until it has. */
    billed_minutes: z.number().int().nullable(),
  }),
  /**
   * The call's recording, present only when the query asks for it (`include_recording=true`);
   * null while there is none: its voice agent does not record calls, the recording is still
   * being finished (call control registers it on the conversation shortly after the call ends),
   * or it could not be finished. Absent from a server that predates it, which reads as none.
   * The record itself is readable until the daily cleanup reaps the ended conversation, the
   * hub's `conversation_retention_days` after it was archived.
   */
  recording: evalCallRecording.nullable().optional(),
  /**
   * The call's page in the web app (`evalCallWebPath`, on the backend's own web origin); null
   * when the backend has no usable web origin. Absent from a server that predates it.
   */
  web_url: z.string().url().nullable().optional(),
});
export type EvalCallRecordResponse = z.infer<typeof evalCallRecordResponse>;

export const evalCallFinishResponse = z.object({
  success: z.literal(true),
  /** The conversation's status after the request: always closed. */
  conversation_status: z.literal('ended'),
});
export type EvalCallFinishResponse = z.infer<typeof evalCallFinishResponse>;
